import { Button } from '@/components/ui/button';
import { ArrowLeft, Video, Mic, Play, AlertCircle, CheckCircle, Loader2, Building2, ShoppingBag, User, Target } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { generateHeyGenSessionToken } from '@/hooks/useVideoEngine';

interface VideoSetupProps {
  scenario: any;
  onStart: () => void;
  onBack: () => void;
}

export function VideoSetup({ scenario, onStart, onBack }: VideoSetupProps) {
  const [hasPermissions, setHasPermissions] = useState(false);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(true);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [isPreparingToken, setIsPreparingToken] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    checkPermissions();
    preFetchToken();
  }, []);

  const preFetchToken = async () => {
    setIsPreparingToken(true);
    try {
      const apiKey = import.meta.env.VITE_HEYGEN_API_TOKEN;
      if (apiKey) {
        console.log('[VideoSetup] Pre-fetching HeyGen token...');
        await generateHeyGenSessionToken(apiKey);
        setHasToken(true);
        console.log('[VideoSetup] Token pre-fetched successfully');
      }
    } catch (err) {
      console.error('[VideoSetup] Error pre-fetching token:', err);
    } finally {
      setIsPreparingToken(false);
    }
  };

  const checkPermissions = async () => {
    setIsCheckingPermissions(true);
    setPermissionError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      stream.getTracks().forEach(track => track.stop());

      setHasPermissions(true);
    } catch (err) {
      setPermissionError('Please allow camera and microphone access to continue.');
      setHasPermissions(false);
    } finally {
      setIsCheckingPermissions(false);
    }
  };

  const handleStart = async () => {
    setIsStarting(true);
    await onStart();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500 rounded-xl mb-4">
                <Video className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-1">
                Video Practice Session
              </h1>
              <p className="text-sm text-gray-500">
                {scenario?.scenario?.product?.company || scenario?.company_name} • {scenario?.scenario?.product?.name || scenario?.product_name}
              </p>
            </div>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <User className="h-4 w-4 text-gray-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Customer</p>
                      <p className="text-sm text-gray-900 truncate">
                        {scenario?.persona?.profile?.name || 'Customer'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {scenario?.persona?.profile?.occupation || 'Professional'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <Target className="h-4 w-4 text-gray-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Objective</p>
                      <p className="text-sm text-gray-900">
                        {scenario?.scenario?.objective?.split('.')[0] || 'Build rapport'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {hasPermissions ? (
                        <CheckCircle className="h-4 w-4 text-blue-500" />
                      ) : isCheckingPermissions ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-amber-500" />
                      )}
                      <span className="text-sm text-gray-700">Camera & Microphone</span>
                    </div>
                    <span className={`text-xs font-medium ${
                      hasPermissions ? 'text-blue-500' : isCheckingPermissions ? 'text-gray-400' : 'text-amber-500'
                    }`}>
                      {isCheckingPermissions ? 'Checking' : hasPermissions ? 'Ready' : 'Required'}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {hasToken ? (
                        <CheckCircle className="h-4 w-4 text-blue-500" />
                      ) : isPreparingToken ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : (
                        <Video className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="text-sm text-gray-700">Video Engine</span>
                    </div>
                    <span className={`text-xs font-medium ${
                      hasToken ? 'text-blue-500' : 'text-gray-400'
                    }`}>
                      {hasToken ? 'Ready' : isPreparingToken ? 'Preparing' : 'Initializing'}
                    </span>
                  </div>
                </div>
              </div>

              {permissionError && (
                <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800">{permissionError}</p>
                    <button
                      onClick={checkPermissions}
                      className="text-xs text-amber-600 hover:text-amber-700 font-medium mt-1"
                    >
                      Try again
                    </button>
                  </div>
                </div>
              )}


              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={onBack}
                  className="flex-1"
                  disabled={isStarting}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleStart}
                  disabled={!hasPermissions || isCheckingPermissions || isStarting}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isStarting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Starting...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Start Practice
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}